import type { Drama } from '@/mocks/dramas';

export default function ConfirmSection({ drama }: { drama: Drama }) {
  return (
    <>
      <section id="confirm" className="scroll-mt-28">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-label text-xs text-foreground-400">01</span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            真实事件改编确认
          </h2>
        </div>

        <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
              判定：{drama.confirmLevel}
            </span>
            <span className="text-xs text-foreground-500">
              核对日期 {drama.publishDate}
            </span>
          </div>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
            {drama.confirmNote}
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-md bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500 mb-1.5 flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                事件时间段
              </p>
              <p className="text-sm text-foreground-900">{drama.eventTime}</p>
            </div>
            <div className="p-4 rounded-md bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500 mb-1.5 flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                事件发生地
              </p>
              <p className="text-sm text-foreground-900">{drama.eventPlace}</p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
            真实事件梗概
          </h3>
          <p className="text-sm md:text-base text-foreground-600 leading-loose">{drama.realEvent}</p>
        </div>

        <div className="mt-6">
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
            这份档案的几个要点
          </h3>
          <ul className="space-y-2.5">
            {drama.highlights.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-sm text-foreground-600">
                <i className="ri-record-circle-line text-accent-500 mt-0.5"></i>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 p-5 rounded-lg bg-accent-50 border-l-4 border-accent-500">
          <p className="font-heading text-sm font-bold text-accent-950 mb-2">档案综述</p>
          <p className="text-sm text-foreground-700 leading-loose">{drama.summary}</p>
        </div>
      </section>

      <section id="characters" className="scroll-mt-28 pt-2">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-label text-xs text-foreground-400">02</span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            剧情原型人物对照
          </h2>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed mb-5">
          剧中的名字几乎都不是真名。下表把主要角色与原型身份对应起来，并标明是单一个体还是多人合并——遇到「多人合并」就不要对号入座。
        </p>

        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="hidden md:table-header-group">
              <tr className="bg-background-100">
                <th className="px-5 py-3.5 text-xs font-medium text-foreground-600 w-40">剧中角色</th>
                <th className="px-5 py-3.5 text-xs font-medium text-foreground-600 w-52">对应原型</th>
                <th className="px-5 py-3.5 text-xs font-medium text-foreground-600">处理说明</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {drama.characters.map((person) => (
                <tr key={person.role} className="block md:table-row bg-background-50 hover:bg-background-100/70 transition-colors">
                  <td className="block md:table-cell px-5 pt-4 md:py-4">
                    <span className="md:hidden text-[11px] text-foreground-400 block mb-1">剧中角色</span>
                    <span className="font-heading text-sm font-bold text-foreground-950">{person.role}</span>
                  </td>
                  <td className="block md:table-cell px-5 pt-2 md:py-4">
                    <span className="md:hidden text-[11px] text-foreground-400 block mb-1">对应原型</span>
                    <span className="inline-block px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                      {person.prototype}
                    </span>
                  </td>
                  <td className="block md:table-cell px-5 pb-4 pt-2 md:py-4">
                    <span className="md:hidden text-[11px] text-foreground-400 block mb-1">处理说明</span>
                    <span className="text-sm text-foreground-600 leading-relaxed">{person.note}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}